import { isImmutable, Map as ImmutableMap, OrderedMap } from 'immutable';

import type { RecordOfObjectEntity, RecordOfObjectsState } from '../../objects/types';
import type { BackendScores, RecordOfMatchEntity, SSRMatchEntity, TransformedMatch } from '../types';

import { PeriodEntity, TimerEntity } from '../records';
import { transformPeriod, transformTimer } from '../transform';

// ---

export function createImmutableObjects(
  matchObjects: TransformedMatch['objects'] | RecordOfMatchEntity['objects'],
  objectsState: RecordOfObjectsState['byId'],
  callback?: (objectId: number, teamId: number) => void,
): RecordOfObjectsState['byId'] {
  let immutableObjects = ImmutableMap<number, RecordOfObjectEntity>();

  if (matchObjects === undefined) {
    return immutableObjects;
  }

  if (matchObjects === null) {
    console.error('message pack error => immutable-structures.ts => createImmutableObjects => matchObjects is ', matchObjects);
    return immutableObjects;
  }

  if (isImmutable(matchObjects)) {
    matchObjects.forEach((objectId, teamId) => {
      const object = objectsState.get(objectId);

      if (object) {
        immutableObjects = immutableObjects.set(teamId, object);
        callback && callback(objectId, teamId);
      }
    });

    return immutableObjects;
  }

  const objectsKeys = Object.keys(matchObjects);

  objectsKeys.forEach((key) => {
    const teamId = parseInt(key);
    const objectId = matchObjects[key];
    const object = objectsState.get(objectId);

    if (object) {
      immutableObjects = immutableObjects.set(teamId, object);
      callback && callback(objectId, teamId);
    }
  });

  return immutableObjects;
}

export function createImmutableMatchObjects(
  matchObjects: TransformedMatch['objects'] | SSRMatchEntity['objects'],
): RecordOfMatchEntity['objects'] {
  const immutableObjects = ImmutableMap<number, number>();

  if (matchObjects === undefined) {
    return immutableObjects;
  }
  if (matchObjects === null) {
    console.error('message pack error => immutable-structures.ts => createImmutableMatchObjects => matchObjects is ', matchObjects);
    return immutableObjects;
  }

  return Object.entries(matchObjects).reduce((acc, [teamId, objectId]) => {
    return acc.set(parseInt(teamId), objectId);
  }, immutableObjects);
}

// ---

export function createImmutableMatchPeriods(
  periods: TransformedMatch['periods'] | SSRMatchEntity['periods'],
): RecordOfMatchEntity['periods'] {
  if (!periods) {
    return ImmutableMap();
  }

  const collectionKeys = Object.keys(periods);

  if (collectionKeys.length === 0) {
    return ImmutableMap();
  }

  const objectsMap = collectionKeys.reduce((map, key) => {
    const transformedPeriod = transformPeriod(periods[key]);

    return map.set(parseInt(key), PeriodEntity(transformedPeriod));
  }, new Map());

  return ImmutableMap(objectsMap);
}

// ---

export function createImmutableMatchTimer(
  timer?: TransformedMatch['timer'] | SSRMatchEntity['timer'],
): RecordOfMatchEntity['timer'] | undefined {
  if (!timer) {
    return undefined;
  }

  const { timestamp, ...props } = transformTimer(timer);

  return TimerEntity({
    ...props,
    timestamp: timestamp ? new Date(timestamp) : undefined,
  });
}

// ---

export function createImmutableMatchScores(
  scores?: TransformedMatch['scores'] | SSRMatchEntity['scores'],
): RecordOfMatchEntity['scores'] {
  if (!scores) {
    return OrderedMap();
  }

  const immutableScores = createRecursiveScores(scores);

  if (typeof immutableScores === 'number' || immutableScores === undefined) {
    return OrderedMap();
  }

  return immutableScores as unknown as RecordOfMatchEntity['scores'];
}

type ImmutableScores = OrderedMap<number, RecursiveScores>;
type RecursiveScores = ImmutableScores | number | undefined;

function createRecursiveScores(
  scores: BackendScores | Record<string, number> | number,
): RecursiveScores {
  if (typeof scores === 'number') {
    return scores;
  }

  const scoresKeys = Object.keys(scores);

  if (scoresKeys.length === 0) {
    return OrderedMap();
  }

  return scoresKeys
    .sort((a, b) => parseInt(a) - parseInt(b))
    .reduce((map, key) => {
      const value = scores[key as keyof typeof scores];

      if (value === undefined) {
        return map;
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const score = createRecursiveScores(value);

      if (score === undefined) {
        return map;
      }

      return map.set(parseInt(key), score);
    }, OrderedMap() as ImmutableScores);
}
